"use client";

import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import { extractYouTubeVideoId } from "@donista/shared/utils/youtube";
import { sanitizeContent } from "@donista/shared/utils/html-to-markdown";

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const CONSENT_TEXT = {
  de: "Bei Klick wird dieses Video von den YouTube Servern geladen.",
  en: "When clicked, this video is loaded from YouTube servers.",
} as const;

function YouTubeEmbed({ videoId }: { videoId: string }) {
  const [consented, setConsented] = useState(false);
  const locale = useLocale();
  const consentText = CONSENT_TEXT[locale === "en" ? "en" : "de"];

  if (consented) {
    return (
      <iframe
        className="w-full aspect-video my-2 rounded-lg"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title="YouTube video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-popups"
        allowFullScreen
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={consentText}
      onClick={() => setConsented(true)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setConsented(true); } }}
      className="relative block w-full aspect-video my-2 rounded-lg overflow-hidden group cursor-pointer bg-neutral-900"
    >
      <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 group-hover:bg-white/5 transition-colors">
        <svg className="w-16 h-16 drop-shadow-lg" viewBox="0 0 68 48" aria-hidden="true">
          <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="#f00" />
          <path d="M45 24 27 14v20" fill="#fff" />
        </svg>
        <span className="text-white text-sm px-4 text-center block">{consentText}</span>
      </span>
    </span>
  );
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <div className={cn("prose prose-sm max-w-none", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            const videoId = href ? extractYouTubeVideoId(href) : null;
            if (videoId) return <YouTubeEmbed videoId={videoId} />;
            return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
          },
        }}
      >
        {sanitizeContent(content)}
      </ReactMarkdown>
    </div>
  );
}
